import { useCallback, useMemo, useState } from 'react';
import { LatLng, MapLocation } from 'shared/types';

export type MarkerEditLocationArg =
  | MapLocation
  | { lat: number; lng: number }
  | null;

export type MarkerEditOnLocationChange = (
  location: MarkerEditLocationArg,
  urlAddress?: string | null,
) => void | Promise<void>;

type UseMarkerEditLockProps = {
  onCommit: MarkerEditOnLocationChange;
  isEditing: boolean;
  onEnterEditMode?: () => void;
  onStage?: (latLng: LatLng) => void;
  onCancel?: () => void;
};

type StagedChange = {
  location: MarkerEditLocationArg;
  urlAddress?: string | null;
};

export type UseMarkerEditLockResult = {
  enabled: boolean;
  isLocked: boolean;
  hasStagedChange: boolean;
  stagedLatLng: LatLng | null;
  handleLocationChange: MarkerEditOnLocationChange;
  enterEditMode: () => void;
  cancel: () => void;
  commit: () => Promise<void>;
};

export const NOOP_MARKER_EDIT_LOCK: UseMarkerEditLockResult = {
  enabled: false,
  isLocked: false,
  hasStagedChange: false,
  stagedLatLng: null,
  handleLocationChange: () => Promise.resolve(),
  enterEditMode: () => {},
  cancel: () => {},
  commit: () => Promise.resolve(),
};

const extractLatLng = (location: MarkerEditLocationArg): LatLng | null =>
  typeof location?.lat === 'number' && typeof location?.lng === 'number'
    ? { lat: location.lat, lng: location.lng }
    : null;

export const useMarkerEditLock = (
  props: UseMarkerEditLockProps,
): UseMarkerEditLockResult => {
  const { onCommit, isEditing, onEnterEditMode, onStage, onCancel } = props;
  const [isLocked, setIsLocked] = useState(true);
  const [staged, setStaged] = useState<StagedChange | null>(null);

  const handleLocationChange = useCallback<MarkerEditOnLocationChange>(
    async (location, urlAddress) => {
      if (!isEditing) {
        await onCommit(location, urlAddress);
        return;
      }
      if (isLocked) return;
      setStaged({ location, urlAddress });
      const latLng = extractLatLng(location);
      if (latLng) onStage?.(latLng);
    },
    [isEditing, isLocked, onCommit, onStage],
  );

  const enterEditMode = useCallback(() => {
    setStaged(null);
    setIsLocked(false);
    onEnterEditMode?.();
  }, [onEnterEditMode]);

  const cancel = useCallback(() => {
    setStaged(null);
    setIsLocked(true);
    onCancel?.();
  }, [onCancel]);

  const commit = useCallback(async () => {
    if (staged?.location) {
      await onCommit(staged.location, staged.urlAddress);
    }
    setStaged(null);
    setIsLocked(true);
  }, [onCommit, staged]);

  const stagedLatLng = useMemo(
    () => (staged ? extractLatLng(staged.location) : null),
    [staged],
  );

  return {
    enabled: isEditing,
    isLocked: isEditing ? isLocked : false,
    hasStagedChange: Boolean(stagedLatLng),
    stagedLatLng,
    handleLocationChange,
    enterEditMode,
    cancel,
    commit,
  };
};
